/**
 * Button Component
 * 
 * Pure presentation component for button interactions.
 * Uses design tokens only, no inline styles.
 */

import type { ReactNode, MouseEvent } from 'react';

export interface ButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

const variantClasses = {
  primary: 'bg-[var(--color-accent-500)] text-white hover:bg-[var(--color-accent-600)]',
  secondary: 'bg-[var(--surface-bg-panel)] text-[var(--surface-text-primary)] hover:bg-[var(--surface-bg-hover)]',
  ghost: 'bg-transparent text-[var(--surface-text-primary)] hover:bg-[var(--surface-bg-hover)]',
  danger: 'bg-[var(--color-error-500)] text-white hover:bg-[var(--color-error-600)]',
};

const sizeClasses = {
  sm: 'px-[var(--space-3)] py-[var(--space-1)] text-[var(--text-sm)]',
  md: 'px-[var(--space-4)] py-[var(--space-2)] text-[var(--text-base)]',
  lg: 'px-[var(--space-6)] py-[var(--space-3)] text-[var(--text-lg)]',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  type = 'button',
  className = '',
  onClick,
}: ButtonProps) {
  const baseClasses = 'rounded-[var(--radius-lg)] font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`.trim();

  return (
    <button
      type={type}
      disabled={disabled}
      className={classes}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
